import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { Card, CardContent, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import _ from 'lodash';

const columns: GridColDef[] = [
  {
    field: 'created_at',
    headerName: 'Time',
    minWidth: 200,
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toUTC().toISO();
    },
  },
  {
    field: 'username',
    headerName: 'Username',
    width: 200,
    disableColumnMenu: true,
  },
  // Command
  {
    field: 'url_path',
    headerName: 'Command',
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
  },
  // Body
  {
    field: 'request_body',
    headerName: 'Body',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  // Params
  {
    field: 'query_parameters',
    headerName: 'Params',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  { field: 'account_id' },
  { field: 'iam_user_id' },
];

const columnVisible = {
  created_at: true,
  account_id: false,
  username: true,
  iam_user_id: false,
  url_path: true,
  query_parameters: true,
  request_body: true
};

export const Histories = () => {
  const api = useApi();

  const [logs, setLogs] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  const { isLoading, data, refetch } = useQuery(
    ['apiLogs'],
    api.getApiLogApiV1GetApiLogGet.bind(null, {}),
    { enabled: true },
  );

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed View Changes.');
  };

  useEffect(() => {
    if (data?.data && data.data.length) {
      let sortedData = _.orderBy(data.data, ['created_at'], ['desc']);
      setLogs(sortedData.map((item, idx) => ({
        ...item,
        username: item.iam_user_username ? item.iam_user_username : item.account_username,
        id: idx,
        url_path: item.url_path?.replace('/api/v1', '')
      })));
    }
  }, [data]);

  return (
    <PageContent>
      <PageHeader
        pageName="View Changes"
        count={logs?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={logs}
              columns={columns}
              columnVisibilityModel={columnVisible}
              refreshData={handleDataRefetch}
              pagination={true}
              onRowClick={(params) => setSelectedRow(params.row)}
            />
            <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} maxWidth="md" fullWidth>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Log Detail
                <IconButton onClick={() => setSelectedRow(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <pre style={{ margin: 0, overflowX: 'auto', fontSize: '1.5rem', lineHeight: 1.7, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6, color: '#000' }}>
                  {selectedRow && (() => {
                    const expanded = Object.fromEntries(
                      Object.entries(selectedRow).map(([k, v]) => {
                        if (typeof v === 'string') {
                          try { return [k, JSON.parse(v)]; } catch { /* not JSON */ }
                        }
                        return [k, v];
                      }),
                    );
                    return JSON.stringify(expanded, null, 2).split('\n').map((line, i) => (
                      <div key={i} style={line.includes('"url_path"') ? { fontWeight: 'bold' } : undefined}>{line}</div>
                    ));
                  })()}
                </pre>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </PageContent>
  );
};
